import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Save, Send } from "lucide-react";
import { toast } from "sonner";

export default function QuotationCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({
    customer_id: "",
    company_id: "",
    type: "NON_ORGANIC",
    validity_days: 15,
    notes: "",
  });
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/customers"),
      api.get("/admin/companies"),
    ]).then(([custRes, compRes]) => {
      setCustomers(custRes.data.items || []);
      setCompanies(compRes.data || []);
    }).catch(() => {});

    // Pre-fill from inquiry
    const item = location.state?.item;
    if (item) {
      setLines([{
        item_code: item.item_code,
        item_name: item.item_name,
        batch: item.recommended_batch || "",
        quantity: 1,
        unit_price: item.base_price || 0,
        discount_pct: 0,
      }]);
      if (item.company_id) setForm((f) => ({ ...f, company_id: item.company_id }));
    }
  }, [location.state]);

  const addLine = () => {
    setLines([...lines, { item_code: "", item_name: "", batch: "", quantity: 1, unit_price: 0, discount_pct: 0 }]);
  };

  const updateLine = (index, field, value) => {
    const updated = [...lines];
    updated[index] = { ...updated[index], [field]: value };
    setLines(updated);
  };

  const removeLine = (index) => {
    setLines(lines.filter((_, i) => i !== index));
  };

  const lineTotal = (line) => {
    const qty = parseFloat(line.quantity) || 0;
    const price = parseFloat(line.unit_price) || 0;
    const disc = parseFloat(line.discount_pct) || 0;
    return qty * price * (1 - disc / 100);
  };

  const grandTotal = lines.reduce((sum, l) => sum + lineTotal(l), 0);

  const handleSave = async (submit = false) => {
    if (!form.customer_id || !form.company_id) {
      toast.error("Please select customer and company");
      return;
    }
    if (lines.length === 0) {
      toast.error("Please add at least one line item");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        lines: lines.map((l) => ({
          ...l,
          quantity: parseFloat(l.quantity) || 0,
          unit_price: parseFloat(l.unit_price) || 0,
          discount_pct: parseFloat(l.discount_pct) || 0,
        })),
      };
      const res = await api.post("/quotations", payload);
      if (submit) {
        await api.post(`/quotations/${res.data.id}/submit`);
        toast.success("Quotation created and submitted for approval");
      } else {
        toast.success("Quotation saved as draft");
      }
      navigate("/quotations");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save quotation");
    }
    setSaving(false);
  };

  return (
    <div data-testid="quotation-create-page" className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B]" style={{ fontFamily: "Chivo, sans-serif" }}>
          New Quotation
        </h1>
        <p className="text-sm text-gray-500 mt-1">Create a new sales quotation</p>
      </div>

      <Card className="border border-gray-200 shadow-sm rounded-sm">
        <CardContent className="p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">Customer</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="quotation-customer-select" className="mt-1.5 rounded-sm h-9">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">Company</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="quotation-company-select" className="mt-1.5 rounded-sm h-9">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NON_ORGANIC">Non-Organic (Import)</SelectItem>
                  <SelectItem value="ORGANIC">Organic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">Validity (days)</Label>
              <Input
                type="number"
                value={form.validity_days}
                onChange={(e) => setForm({ ...form, validity_days: parseInt(e.target.value) || 15 })}
                className="mt-1.5 rounded-sm h-9"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">Notes</Label>
            <Textarea
              data-testid="quotation-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1.5 rounded-sm"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border border-gray-200 shadow-sm rounded-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-gray-500">Line Items</h3>
            <Button data-testid="add-line-button" variant="outline" size="sm" onClick={addLine} className="rounded-sm">
              <Plus size={14} className="mr-1" /> Add Item
            </Button>
          </div>

          {lines.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">No line items. Click "Add Item" to begin.</p>
          )}

          {lines.map((line, idx) => (
            <div key={idx} data-testid={`line-item-${idx}`} className="grid grid-cols-12 gap-2 mb-3 items-end">
              <div className="col-span-2">
                {idx === 0 && <Label className="text-[10px] uppercase tracking-wider text-gray-400">Item Code</Label>}
                <Input value={line.item_code} onChange={(e) => updateLine(idx, "item_code", e.target.value)} className="rounded-sm h-8 text-sm" />
              </div>
              <div className="col-span-3">
                {idx === 0 && <Label className="text-[10px] uppercase tracking-wider text-gray-400">Item Name</Label>}
                <Input value={line.item_name} onChange={(e) => updateLine(idx, "item_name", e.target.value)} className="rounded-sm h-8 text-sm" />
              </div>
              <div className="col-span-1">
                {idx === 0 && <Label className="text-[10px] uppercase tracking-wider text-gray-400">Batch</Label>}
                <Input value={line.batch} onChange={(e) => updateLine(idx, "batch", e.target.value)} className="rounded-sm h-8 text-sm" />
              </div>
              <div className="col-span-1">
                {idx === 0 && <Label className="text-[10px] uppercase tracking-wider text-gray-400">Qty</Label>}
                <Input type="number" value={line.quantity} onChange={(e) => updateLine(idx, "quantity", e.target.value)} className="rounded-sm h-8 text-sm" />
              </div>
              <div className="col-span-2">
                {idx === 0 && <Label className="text-[10px] uppercase tracking-wider text-gray-400">Unit Price</Label>}
                <Input type="number" step="0.01" value={line.unit_price} onChange={(e) => updateLine(idx, "unit_price", e.target.value)} className="rounded-sm h-8 text-sm" />
              </div>
              <div className="col-span-1">
                {idx === 0 && <Label className="text-[10px] uppercase tracking-wider text-gray-400">Disc %</Label>}
                <Input type="number" value={line.discount_pct} onChange={(e) => updateLine(idx, "discount_pct", e.target.value)} className="rounded-sm h-8 text-sm" />
              </div>
              <div className="col-span-1 text-right">
                {idx === 0 && <Label className="text-[10px] uppercase tracking-wider text-gray-400">Total</Label>}
                <div className="h-8 flex items-center justify-end text-sm font-medium">{lineTotal(line).toFixed(2)}</div>
              </div>
              <div className="col-span-1 flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => removeLine(idx)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}

          {lines.length > 0 && (
            <div className="border-t border-gray-200 pt-3 mt-3 flex justify-end">
              <div className="text-right">
                <span className="text-xs uppercase tracking-wider text-gray-500 mr-4">Grand Total</span>
                <span className="text-xl font-bold text-[#09090B]" style={{ fontFamily: "Chivo, sans-serif" }}>
                  {grandTotal.toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        <Button data-testid="cancel-quotation" variant="outline" onClick={() => navigate("/quotations")} className="rounded-sm">
          Cancel
        </Button>
        <Button
          data-testid="save-draft-quotation"
          variant="outline"
          onClick={() => handleSave(false)}
          disabled={saving}
          className="rounded-sm"
        >
          <Save size={14} className="mr-1.5" /> Save Draft
        </Button>
        <Button
          data-testid="submit-quotation"
          onClick={() => handleSave(true)}
          disabled={saving}
          className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm"
        >
          <Send size={14} className="mr-1.5" /> Save & Submit
        </Button>
      </div>
    </div>
  );
}
